/**
 * TemplatesSettings — Admin defaults for the Templates Manager.
 *
 * Tenant-wide configuration for default sender name/email, signature,
 * email footer HTML and auto-archive policy. Only super_admin /
 * crm_admin roles can save. Other roles see the same view but the
 * Save button is disabled.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Save, Settings, Loader2 } from "lucide-react";
import { API, useAuth } from "@/App";

export default function TemplatesSettings({ onBack }) {
  const { token, user } = useAuth();
  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState(false);
  const role = (user?.role || "").toLowerCase();
  const isAdmin = ["super_admin", "crm_admin"].includes(role);
  // Read-only and non-CRM-mutating roles see a Forbidden panel instead
  // of the settings UI (backend also returns 403 for read_only).
  const canViewSettings = !["read_only"].includes(role);

  useEffect(() => {
    if (!canViewSettings) { setValues({}); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/templates/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setValues(r.data);
      } catch {
        toast.error("Couldn't load templates settings");
        setValues({});
      }
    })();
  }, [token, canViewSettings]);

  const save = async () => {
    if (!isAdmin) return;
    setSaving(true);
    try {
      const r = await axios.put(`${API}/templates/settings`, values, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setValues(r.data);
      toast.success("Template defaults saved");
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (e) => setValues({ ...values, [k]: e.target.value });

  if (!values) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Loader2 style={{ width: 16, height: 16, animation: "spin 0.8s linear infinite", color: "var(--text-tertiary)" }} />
      </div>
    );
  }

  if (!canViewSettings) {
    return (
      <div
        data-testid="templates-settings-forbidden"
        style={{
          flex: 1, display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          background: "var(--page-bg,#f2f2f7)", padding: 40, textAlign: "center",
          fontFamily: '"DM Sans", -apple-system, sans-serif',
        }}
      >
        <Settings style={{ width: 28, height: 28, color: "var(--text-tertiary)", marginBottom: 10 }} />
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>You don't have access</div>
        <div style={{ fontSize: 12.5, color: "var(--text-tertiary)", maxWidth: 360, marginBottom: 14 }}>
          Templates settings are limited to admin and CRM editor roles. If you need access, ask a super admin to upgrade your role.
        </div>
        <button
          data-testid="templates-settings-back-forbidden"
          onClick={onBack}
          style={{
            background: "transparent", border: "0.5px solid var(--border)",
            borderRadius: 8, padding: "6px 14px", cursor: "pointer",
            fontSize: 12, fontFamily: "inherit", color: "var(--text-primary)",
          }}
        >
          ← Back to Templates
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="templates-settings-page"
      style={{
        display: "flex", flexDirection: "column", flex: 1,
        background: "var(--page-bg,#f2f2f7)", overflowY: "auto",
        fontFamily: '"DM Sans", -apple-system, sans-serif',
      }}
    >
      <header
        style={{
          padding: "12px 20px", background: "rgba(248,248,250,0.85)",
          backdropFilter: "blur(20px)",
          borderBottom: "0.5px solid rgba(0,0,0,0.08)",
          display: "flex", alignItems: "center", gap: 8,
        }}
      >
        <button
          data-testid="templates-settings-back"
          onClick={onBack}
          style={{
            background: "transparent", border: "0.5px solid var(--border)",
            borderRadius: 8, padding: "5px 10px", cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 4,
            fontSize: 11, fontFamily: "inherit", color: "var(--text-primary)",
          }}
        >
          <ArrowLeft style={{ width: 11, height: 11 }} /> Templates
        </button>
        <Settings style={{ width: 16, height: 16, color: "#0071e3", marginLeft: 6 }} />
        <span style={{ fontSize: 16, fontWeight: 500, letterSpacing: "-0.3px" }}>Templates settings</span>
        <div style={{ flex: 1 }} />
        <button
          data-testid="templates-settings-save"
          onClick={save}
          disabled={!isAdmin || saving}
          style={{
            padding: "6px 12px", borderRadius: 8,
            background: !isAdmin ? "var(--page-bg)" : "#0071e3",
            color: !isAdmin ? "var(--text-tertiary)" : "#fff",
            border: "none", cursor: !isAdmin ? "not-allowed" : "pointer",
            fontSize: 12, fontWeight: 600, fontFamily: "inherit",
            display: "inline-flex", alignItems: "center", gap: 5,
          }}
        >
          {saving ? <Loader2 style={{ width: 12, height: 12, animation: "spin 0.8s linear infinite" }} /> : <Save style={{ width: 12, height: 12 }} />}
          {saving ? "Saving…" : "Save"}
        </button>
      </header>

      <div style={{ maxWidth: 720, padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
        {!isAdmin && (
          <div
            data-testid="templates-settings-readonly"
            style={{
              padding: "10px 14px", borderRadius: 8,
              background: "rgba(255,159,10,0.10)", color: "#8a5500",
              fontSize: 12,
            }}
          >
            You're viewing in read-only mode. Only super admins and CRM admins can change tenant-wide template defaults.
          </div>
        )}

        <Section title="Sender identity"
          hint="What recipients see in the From row of every email sent from this CRM.">
          <Field label="Default sender name">
            <input
              data-testid="settings-sender-name"
              disabled={!isAdmin}
              value={values.default_sender_name || ""}
              onChange={set("default_sender_name")}
              placeholder="Enable Healthcare Sales Team"
              style={inputStyle}
            />
          </Field>
          <Field label="Default sender email">
            <input
              data-testid="settings-sender-email"
              type="email" disabled={!isAdmin}
              value={values.default_sender_email || ""}
              onChange={set("default_sender_email")}
              placeholder="sales@enablehealthcare.com"
              style={inputStyle}
            />
          </Field>
        </Section>

        <Section title="Email signature"
          hint="Appended to every outbound email. HTML is allowed.">
          <textarea
            data-testid="settings-signature"
            disabled={!isAdmin}
            value={values.default_signature_html || ""}
            onChange={set("default_signature_html")}
            rows={6}
            placeholder="<p>Best,<br/>{{user.full_name}}<br/>Enable Healthcare</p>"
            style={{ ...inputStyle, fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 }}
          />
        </Section>

        <Section title="Email footer (compliance)"
          hint="Legal / unsubscribe footer. Required for CAN-SPAM compliance on marketing email.">
          <textarea
            data-testid="settings-footer"
            disabled={!isAdmin}
            value={values.default_footer_html || ""}
            onChange={set("default_footer_html")}
            rows={5}
            placeholder='<p style="font-size:11px;color:#999">Enable Healthcare · 123 Main St · Unsubscribe</p>'
            style={{ ...inputStyle, fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 }}
          />
        </Section>

        <Section title="Auto-archive policy"
          hint="Templates unused for this many days are auto-archived. 0 to disable.">
          <Field label="Days unused before archive">
            <input
              data-testid="settings-auto-archive-days"
              disabled={!isAdmin}
              type="number" min="0" max="365"
              value={values.auto_archive_unused_days ?? 0}
              onChange={(e) => setValues({ ...values, auto_archive_unused_days: parseInt(e.target.value || "0", 10) })}
              style={{ ...inputStyle, maxWidth: 160 }}
            />
          </Field>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, hint, children }) {
  return (
    <div
      style={{
        background: "var(--surface,#fff)",
        border: "0.5px solid var(--border)", borderRadius: 12,
        padding: "16px 18px",
        display: "flex", flexDirection: "column", gap: 10,
      }}
    >
      <div>
        <div style={{ fontSize: 13.5, fontWeight: 600, letterSpacing: "-0.1px" }}>{title}</div>
        {hint && <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 2 }}>{hint}</div>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block" }}>
      <div
        style={{
          fontSize: 10.5, fontWeight: 600, color: "var(--text-tertiary)",
          textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4,
        }}
      >
        {label}
      </div>
      {children}
    </label>
  );
}

const inputStyle = {
  width: "100%", padding: "8px 11px",
  border: "0.5px solid var(--border)", borderRadius: 8,
  fontSize: 12.5, fontFamily: "inherit",
  background: "var(--surface,#fff)", outline: "none",
  boxSizing: "border-box",
};
